import { z } from 'zod';
import type { TeacherIdentity } from './identity';
import { backupDataSchema, validateBackupRelations } from './schemas';
import type { BackupData } from './types';

export const CLOUD_SCHEMA_VERSION = 1;
// D1 strings are limited to 2,000,000 bytes. Keep room for the update envelope and UTF-8 JSON.
export const CLOUD_CONFIG_BODY_LIMIT_BYTES = 1_750_000;

export const cloudSchoolDataSchema = backupDataSchema.superRefine((data, context) => {
  for (const issue of validateBackupRelations(data)) {
    context.addIssue({
      code: 'custom',
      message: issue.code,
      path: issue.path.split('.'),
    });
  }
});

export const cloudConfigUpdateSchema = z.object({
  expectedRevision: z.number().int().min(0),
  data: cloudSchoolDataSchema,
});

export const cloudLoginSchema = z.object({
  pin: z
    .string()
    .trim()
    .regex(/^\d{6,12}$/),
});

export const cloudPinChangeSchema = z
  .object({
    currentPin: z
      .string()
      .trim()
      .regex(/^\d{6,12}$/),
    newPin: z
      .string()
      .trim()
      .regex(/^\d{6,12}$/),
  })
  .refine(({ currentPin, newPin }) => currentPin !== newPin);

export interface CloudConfigSnapshot {
  data: BackupData;
  revision: number;
  updatedAt: string;
  schemaVersion: typeof CLOUD_SCHEMA_VERSION;
}

export interface CloudHealthResponse {
  ok: true;
  service: 'waschpilot-cloud';
  schemaVersion: typeof CLOUD_SCHEMA_VERSION;
  configured: boolean;
  teacherPinConfigured: boolean;
  database: true;
  migrationVersion?: number;
  timestamp: string;
}

export interface CloudLoginResponse {
  user?: TeacherIdentity;
  authenticated: true;
  csrfToken: string;
  expiresAt: string;
}

export type CloudSessionResponse =
  | CloudLoginResponse
  | {
      authenticated: false;
    };

export type CloudErrorCode =
  | 'forbidden'
  | 'invalid_link'
  | 'account_exists'
  | 'bad_request'
  | 'body_too_large'
  | 'csrf_failed'
  | 'database_unavailable'
  | 'forbidden_origin'
  | 'invalid_credentials'
  | 'not_authenticated'
  | 'not_configured'
  | 'rate_limited'
  | 'revision_conflict'
  | 'server_misconfigured'
  | 'stored_config_invalid';

export interface CloudErrorResponse {
  error: {
    code: CloudErrorCode;
    message: string;
  };
  current?: CloudConfigSnapshot;
}

/**
 * Device preferences never participate in cloud synchronisation. Keeping canonical values on the
 * wire lets the existing BackupData validator remain the single source of truth while clients
 * merge their own preferences after fetching a snapshot.
 */
export function canonicalizeCloudSchoolData(data: BackupData): BackupData {
  const parsed = cloudSchoolDataSchema.parse(data);
  const settings = parsed.settings[0]!;
  return {
    ...structuredClone(parsed),
    settings: [
      {
        ...settings,
        language: 'de',
        theme: 'system',
        welcomeDismissed: false,
      },
    ],
  };
}
